/**
 * Configurazione industry — OB.1 Sector Pack.
 *
 * Definisce le 8 industry supportate con etichette, descrizioni dettagliate,
 * file richiesti (core_minimo) e consigliati (core_esteso) per il wizard onboarding.
 */

export type IndustrySlug =
  | 'servizi_professionali'
  | 'saas_msp'
  | 'manifatturiero'
  | 'logistica_trasporti'
  | 'field_service'
  | 'agenzie_marketing'
  | 'distribuzione_wholesale'
  | 'system_integrator'

export interface FileRequired {
  table: string          // nome tabella canonical
  label: string          // etichetta UI
  description: string    // breve descrizione per l'utente
}

export interface IndustryConfig {
  slug: IndustrySlug
  label: string
  tagline: string        // headline breve (max 6 parole)
  description: string    // descrizione estesa per aiutare l'utente a riconoscersi
  examples: string[]     // esempi di aziende/settori tipici
  emoji: string
  core_minimo: FileRequired[]
  core_esteso: FileRequired[]
}

// ─────────────────────────────────────────────────────────────────────────────
// File canonical — descrizioni riutilizzabili
// ─────────────────────────────────────────────────────────────────────────────

const F_ORDERS: FileRequired = {
  table: 'orders',
  label: 'Commesse / Ordini di vendita',
  description: 'Ogni commessa o contratto aperto, con valore, cliente e stato avanzamento.',
}
const F_HOURS: FileRequired = {
  table: 'production_hours',
  label: 'Ore produttive / Timesheet',
  description: 'Le ore lavorate per commessa o progetto, per risorsa o reparto.',
}
const F_INVOICES: FileRequired = {
  table: 'invoices',
  label: 'Fatture emesse e incassi',
  description: 'Tutte le fatture con importo, data, stato pagamento e scadenza.',
}
const F_RECURRING: FileRequired = {
  table: 'recurring_components',
  label: 'Contratti e canoni ricorrenti',
  description: 'Abbonamenti, canoni MRR/ARR e contratti a rinnovo automatico.',
}
const F_REVENUE_REC: FileRequired = {
  table: 'revenue_recognition',
  label: 'Ricavi competenziati',
  description: 'Ricavi riconosciuti per competenza (rilevante per fatturazione a SAL o a ore).',
}
const F_COSTS: FileRequired = {
  table: 'cost_entries',
  label: 'Costi e OPEX extra',
  description: 'Costi fissi, variabili e overhead non rilevati dalle ore (fornitori, affitti, ecc.).',
}

// ─────────────────────────────────────────────────────────────────────────────
// Configurazione per settore
// ─────────────────────────────────────────────────────────────────────────────

export const INDUSTRY_CONFIGS: IndustryConfig[] = [
  {
    slug: 'servizi_professionali',
    label: 'Servizi professionali',
    tagline: 'Consulenza, studi e agenzie',
    description:
      'Vendi competenza e tempo: la tua marginalità dipende da quanto usi le ore disponibili ' +
      'e da quanto riesci a fatturare per ogni ora lavorata. Koremargin traccia utilization, ' +
      'margine per commessa e DSO per mantenere la liquidità sotto controllo.',
    examples: ['Società di consulenza', 'Studi legali e commercialisti', 'Agenzie digitali', 'Società di ingegneria'],
    emoji: '🧑‍💼',
    core_minimo: [F_ORDERS, F_HOURS, F_INVOICES],
    core_esteso: [F_REVENUE_REC, F_COSTS],
  },
  {
    slug: 'saas_msp',
    label: 'Software / SaaS / MSP',
    tagline: 'Ricavi ricorrenti e abbonamenti',
    description:
      'Il tuo motore di crescita è il ricavo ricorrente (MRR/ARR). Koremargin monitora ' +
      'Net Revenue Retention, churn, burn rate e runway per darti la visione CFO-grade ' +
      'che serve agli investitori e al board.',
    examples: ['Software house con licenze SaaS', 'Managed Service Provider', 'Vendor di soluzioni cloud', 'Piattaforme B2B in abbonamento'],
    emoji: '💻',
    core_minimo: [F_RECURRING, F_INVOICES],
    core_esteso: [F_ORDERS, F_HOURS, F_COSTS],
  },
  {
    slug: 'manifatturiero',
    label: 'Manifatturiero / Produzione',
    tagline: 'Commesse, reparti e lavorazioni',
    description:
      'Produci beni su commessa o in serie: la redditività si misura per commessa e per reparto. ' +
      'Koremargin traccia le ore macchina, il costo di trasformazione e il margine per linea di prodotto, ' +
      'con alert sulle commesse in perdita prima che sia troppo tardi.',
    examples: ['Terzisti meccanici e metalmeccanici', 'Impianti su commessa', 'Lavorazioni conto terzi', 'Stampisti e fonderie'],
    emoji: '🏭',
    core_minimo: [F_ORDERS, F_HOURS, F_INVOICES],
    core_esteso: [F_COSTS, F_REVENUE_REC],
  },
  {
    slug: 'logistica_trasporti',
    label: 'Logistica e trasporti',
    tagline: 'Spedizioni, vettori e tratte',
    description:
      'Il margine si gioca sulla tratta, sul vettore e sul volume. Koremargin analizza ' +
      'il fatturato per rotta e per cliente, il costo del lavoro degli autisti e i tempi ' +
      'di incasso, con DSO e overdue sempre in primo piano.',
    examples: ['Corrieri e spedizionieri', 'Trasporto merci conto terzi', 'Operatori logistici', 'Magazzini e centri distribuzione'],
    emoji: '🚛',
    core_minimo: [F_ORDERS, F_INVOICES],
    core_esteso: [F_HOURS, F_COSTS],
  },
  {
    slug: 'field_service',
    label: 'Field Service / Assistenza tecnica',
    tagline: 'Interventi, manutenzione e SLA',
    description:
      'Eroghi assistenza sul campo: ogni ticket è un centro di costo con ore tecnico, ' +
      'trasferta e ricambi. Koremargin monitora la redditività per contratto di manutenzione, ' +
      'il costo medio intervento e la fatturabilità del personale tecnico.',
    examples: ['Manutentori di impianti industriali', 'Assistenza tecnica on-site', 'Installatori e certificatori', 'Service post-vendita'],
    emoji: '🔧',
    core_minimo: [F_ORDERS, F_HOURS, F_INVOICES],
    core_esteso: [F_RECURRING, F_COSTS],
  },
  {
    slug: 'agenzie_marketing',
    label: 'Agenzie marketing e comunicazione',
    tagline: 'Campagne, creatività e media',
    description:
      'Gestisci campagne per più clienti in parallelo: la sfida è tenere il budget ore ' +
      'sotto controllo commessa per commessa. Koremargin traccia le ore creative per progetto, ' +
      'il margine cliente e la pipeline commerciale in un\'unica vista.',
    examples: ['Agenzie di comunicazione integrata', 'Agenzie SEO/SEM e digital', 'Agenzie PR e ufficio stampa', 'Media agency'],
    emoji: '📣',
    core_minimo: [F_ORDERS, F_HOURS, F_INVOICES],
    core_esteso: [F_COSTS, F_REVENUE_REC],
  },
  {
    slug: 'distribuzione_wholesale',
    label: 'Distribuzione e wholesale',
    tagline: 'Rivendita, margini e rete vendita',
    description:
      'Compri e rivendi: la marginalità dipende dal mix di prodotto, dai termini ' +
      'di pagamento e dall\'efficienza della rete vendita. Koremargin calcola il ' +
      'margine lordo per categoria, il DSO per cliente e la performance per agente.',
    examples: ['Grossisti e importatori', 'Distributori di settore', 'Dealer e rivenditori autorizzati', 'Trader B2B'],
    emoji: '📦',
    core_minimo: [F_ORDERS, F_INVOICES],
    core_esteso: [F_COSTS, F_RECURRING],
  },
  {
    slug: 'system_integrator',
    label: 'System Integrator (IT)',
    tagline: 'Progetti infrastruttura e implementazioni',
    description:
      'Integri sistemi complessi su progetti plurimesili: il rischio è lo sforamento ' +
      'di ore e il ritardo nel riconoscimento ricavi. Koremargin traccia il burndown ' +
      'ore per progetto, le milestone di fatturazione e il margine a completamento.',
    examples: ['Integratori ERP e CRM', 'Implementatori SAP/Oracle/Microsoft', 'Consulenti infrastruttura IT', 'Partner di soluzioni cloud enterprise'],
    emoji: '🖥️',
    core_minimo: [F_ORDERS, F_HOURS, F_INVOICES],
    core_esteso: [F_RECURRING, F_REVENUE_REC, F_COSTS],
  },
]

export const INDUSTRY_MAP: Record<IndustrySlug, IndustryConfig> = Object.fromEntries(
  INDUSTRY_CONFIGS.map(c => [c.slug, c]),
) as Record<IndustrySlug, IndustryConfig>

export function getIndustryLabel(slug: string | null | undefined): string {
  if (!slug) return '—'
  return INDUSTRY_MAP[slug as IndustrySlug]?.label ?? slug
}
